<template>
  <div class="td-view">
    <TechnicianTopBar
      :title="ticket ? ticket.code : 'Ticket'"
      :priority="ticket ? PRIORITY_INFO[ticket.priority] : null"
      back
      @back="goBack"
    />

    <div v-if="loading" class="td-state"><span class="mdi mdi-loading td-state__spinner"></span></div>

    <div v-else-if="loadError" class="td-state td-state--message">
      <span class="mdi mdi-alert-circle-outline td-state__icon"></span>
      <p>{{ loadError }}</p>
      <button class="td-btn td-btn--outline td-btn--inline" @click="goBack">Volver a mis tickets</button>
    </div>

    <div v-else class="td-body" ref="bodyEl">
      <div class="td-column">
        <!-- Descripción -->
        <section class="td-block">
          <h1 class="td-title">{{ ticket.title }}</h1>
          <p v-if="ticket.description" class="td-description">{{ ticket.description }}</p>
          <p v-else class="td-description td-description--empty">Sin descripción adicional.</p>

          <div v-if="ticket.photos.length" class="td-gallery">
            <img
              v-for="photo in ticket.photos"
              :key="photo.id"
              :src="photoSrc(photo.filePath)"
              class="td-gallery__thumb"
              alt="Foto del ticket"
              @click="lightbox = photoSrc(photo.filePath)"
            />
          </div>
        </section>

        <!-- Ubicación -->
        <section class="td-block td-location">
          <div class="td-location__info">
            <div class="td-location__tenant">{{ ticket.tenantName || 'Sin inquilino vigente' }}</div>
            <div class="td-location__meta">
              <span class="td-tag">{{ ticket.location.plotName }}</span>
              <span v-if="ticket.location.zone">{{ ticket.location.zone }}</span>
            </div>
            <div v-if="ticket.location.accessNotes" class="td-location__notes">{{ ticket.location.accessNotes }}</div>
          </div>
          <a v-if="mapsUrl" :href="mapsUrl" target="_blank" rel="noopener" class="td-location__map">Cómo llegar</a>
        </section>

        <!-- Estatus actual -->
        <section class="td-block td-status">
          <span class="td-status__pill" :style="{ backgroundColor: currentPill.color }">{{ currentPill.label }}</span>
          <div class="td-status__date">Asignado el {{ formatShortDateTime(ticket.assignedAt) }}</div>
          <div v-if="ticket.sla.state !== 'none'" class="td-status__sla" :style="{ color: SLA_COLORS[ticket.sla.state] }">
            {{ slaText(ticket.sla) }}
          </div>
        </section>

        <!-- Actualizar estatus -->
        <section ref="statusBlock" class="td-block">
          <div class="td-label">Actualizar estatus</div>

          <div v-if="ticket.status === 'RESOLVED'" class="td-note">Este ticket ya fue resuelto.</div>
          <div v-else-if="ticket.status === 'WAITING'" class="td-note td-note--warn">
            El ticket está en espera ({{ WAIT_REASONS[ticket.waitReason] || 'en pausa' }}). El administrador lo reactivará.
          </div>

          <template v-else>
            <div class="td-stages">
              <button
                v-for="stage in STAGES"
                :key="stage"
                class="td-stage"
                :class="{ 'td-stage--current': ticket.fieldStatus === stage, 'td-stage--selected': selected === stage }"
                :style="stageStyle(stage)"
                @click="selected = stage === ticket.fieldStatus ? null : stage"
              >
                {{ STAGE_INFO[stage].label }}
              </button>
            </div>

            <template v-if="selected === 'RESOLVED'">
              <label class="td-field">
                <span class="td-field__label">Describe la solución aplicada <span class="td-required">*</span></span>
                <textarea v-model="solution" class="td-textarea" rows="4" placeholder="Qué se hizo, qué piezas se cambiaron..."></textarea>
              </label>

              <input ref="fileInput" type="file" accept="image/*" capture="environment" class="td-hidden" @change="onFileSelected" />
              <div class="td-attach">
                <button class="td-btn td-btn--outline" type="button" @click="fileInput?.click()">
                  <span class="mdi mdi-camera-outline"></span>&nbsp;{{ photoFile ? 'Cambiar foto' : 'Adjuntar foto del trabajo' }}
                </button>
                <span v-if="photoFile" class="td-attach__name">{{ photoFile.name }}</span>
              </div>
            </template>

            <div v-if="stageError" class="td-error">{{ stageError }}</div>
            <button
              v-if="selected"
              class="td-btn td-btn--primary td-btn--block"
              :disabled="savingStage || (selected === 'RESOLVED' && !solution.trim())"
              @click="onUpdateStage"
            >
              {{ savingStage ? 'Actualizando…' : 'Actualizar' }}
            </button>
          </template>
        </section>

        <!-- Hilo de mensajes -->
        <section class="td-block">
          <div class="td-label">Mensajes</div>

          <div ref="threadEl" class="td-thread">
            <div v-if="!ticket.messages.length" class="td-note">Aún no hay mensajes en este ticket.</div>
            <template v-for="m in ticket.messages" :key="m.id">
              <div v-if="m.isAutomated" class="td-auto">
                {{ m.author }} · {{ m.text.replace('Actualizó el estatus: ', '') }} · {{ formatShortDateTime(m.createdAt) }}
              </div>
              <div v-else class="td-bubble-row" :class="{ 'td-bubble-row--mine': m.mine }">
                <div class="td-bubble" :class="{ 'td-bubble--mine': m.mine }">
                  <div v-if="!m.mine" class="td-bubble__author">{{ m.author }}</div>
                  <div v-if="m.text" class="td-bubble__text">{{ m.text }}</div>
                  <img
                    v-if="m.attachmentPath && isImage(m.attachmentPath)"
                    :src="photoSrc(m.attachmentPath)"
                    class="td-bubble__img"
                    alt="Adjunto"
                    @click="lightbox = photoSrc(m.attachmentPath)"
                  />
                  <a v-else-if="m.attachmentPath" :href="photoSrc(m.attachmentPath)" target="_blank" rel="noopener" class="td-bubble__file">Ver adjunto</a>
                  <div class="td-bubble__time">{{ formatShortDateTime(m.createdAt) }}</div>
                </div>
              </div>
            </template>
          </div>

          <form class="td-composer" @submit.prevent="onSendMessage">
            <input v-model="draft" type="text" class="td-composer__input" placeholder="Escribe una actualización..." />
            <button type="submit" class="td-composer__send" :disabled="!draft.trim() || sending" aria-label="Enviar">
              <span class="mdi mdi-send"></span>
            </button>
          </form>
          <div v-if="messageError" class="td-error">{{ messageError }}</div>
        </section>

        <!-- Costos (solo si el administrador lo habilitó) -->
        <section v-if="ticket.costTotal !== null" class="td-block td-cost">
          <span class="td-label td-label--inline">Costo registrado</span>
          <span class="td-cost__amount">{{ formatMoney(ticket.costTotal) }}</span>
        </section>
      </div>
    </div>

    <div v-if="lightbox" class="td-lightbox" @click="lightbox = ''">
      <img :src="lightbox" alt="" />
      <button class="td-lightbox__close" aria-label="Cerrar"><span class="mdi mdi-close"></span></button>
    </div>

    <div v-if="toast" class="td-toast"><span class="mdi mdi-check-circle"></span> {{ toast }}</div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TechnicianTopBar from '@/components/Technician/TechnicianTopBar.vue'
import {
  getMyTicketDetail, updateTicketStage, sendTicketMessage,
  PRIORITY_INFO, STAGE_INFO, WAIT_REASONS, SLA_COLORS, slaText, formatShortDateTime, photoSrc,
} from '@/services/technicianPortal'

const route = useRoute()
const router = useRouter()
const STAGES = ['CONFIRMED', 'EN_ROUTE', 'ON_SITE', 'RESOLVED']

const ticketId = computed(() => route.params.id)
const ticket = ref(null)
const loading = ref(true)
const loadError = ref('')

const bodyEl = ref(null)
const statusBlock = ref(null)
const threadEl = ref(null)
const fileInput = ref(null)
const lightbox = ref('')

const selected = ref(null)
const solution = ref('')
const photoFile = ref(null)
const savingStage = ref(false)
const stageError = ref('')

const draft = ref('')
const sending = ref(false)
const messageError = ref('')

const toast = ref('')
let toastTimer = null
function showToast(message) {
  toast.value = message
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => { toast.value = '' }, 3000)
}

const LOAD_ERRORS = {
  TICKET_NOT_FOUND: 'No encontramos este ticket entre los que tienes asignados.',
  PORTAL_DISABLED: 'El portal de técnicos no está habilitado en este parque.',
}

async function load() {
  try {
    ticket.value = await getMyTicketDetail(ticketId.value)
    loadError.value = ''
  } catch (err) {
    loadError.value = LOAD_ERRORS[err.message] || 'No se pudo cargar el ticket.'
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.push('/tecnico')
}

const currentPill = computed(() => {
  const t = ticket.value
  if (t.status === 'RESOLVED') return STAGE_INFO.RESOLVED
  if (t.status === 'WAITING') return { label: 'En espera', color: '#64748b' }
  if (!t.fieldStatus) return { label: 'Sin confirmar', color: '#94a3b8' }
  return STAGE_INFO[t.fieldStatus]
})

// El estatus actual va en sólido con su color; los demás en outline (el seleccionado se resalta).
function stageStyle(stage) {
  const color = STAGE_INFO[stage].color
  const solid = ticket.value.fieldStatus === stage
  const picked = selected.value === stage
  return {
    borderColor: color,
    color: solid ? '#fff' : color,
    backgroundColor: solid ? color : picked ? `${color}22` : '#fff',
    boxShadow: picked ? `0 0 0 2px ${color}55` : 'none',
  }
}

const mapsUrl = computed(() => {
  const loc = ticket.value?.location
  if (!loc) return ''
  if (loc.latitude != null && loc.longitude != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}`
  }
  if (loc.address) return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(loc.address)}`
  return ''
})

const isImage = (path) => /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(path || '')
const formatMoney = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0)

function onFileSelected(event) {
  photoFile.value = event.target.files?.[0] || null
}

const STAGE_ERRORS = {
  SOLUTION_REQUIRED: 'Describe la solución aplicada para poder resolver el ticket.',
  TICKET_ALREADY_RESOLVED: 'Este ticket ya fue resuelto.',
  TICKET_ON_HOLD: 'El ticket está en espera; lo reactiva el administrador.',
  INVALID_IMAGE_TYPE: 'La foto debe ser JPG, PNG, WebP o HEIC.',
  IMAGE_TOO_LARGE: 'La foto no debe pasar de 8 MB.',
}

async function onUpdateStage() {
  stageError.value = ''
  savingStage.value = true
  try {
    await updateTicketStage(ticketId.value, selected.value, { solution: solution.value.trim(), file: photoFile.value })
    const wasResolved = selected.value === 'RESOLVED'
    selected.value = null
    solution.value = ''
    photoFile.value = null
    await load()
    showToast(wasResolved ? 'Ticket resuelto' : 'Estatus actualizado')
  } catch (err) {
    stageError.value = STAGE_ERRORS[err.message] || 'No se pudo actualizar el estatus.'
  } finally {
    savingStage.value = false
  }
}

async function scrollThreadToEnd() {
  await nextTick()
  if (threadEl.value) threadEl.value.scrollTop = threadEl.value.scrollHeight
}

async function onSendMessage() {
  const text = draft.value.trim()
  if (!text) return
  messageError.value = ''
  sending.value = true
  try {
    const message = await sendTicketMessage(ticketId.value, text)
    ticket.value.messages.push(message)
    draft.value = ''
    scrollThreadToEnd()
  } catch {
    messageError.value = 'No se pudo enviar el mensaje. Intenta de nuevo.'
  } finally {
    sending.value = false
  }
}

onMounted(async () => {
  await load()
  await nextTick()
  scrollThreadToEnd()
  if (route.query.focus === 'status' && statusBlock.value) {
    statusBlock.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
})
</script>

<style scoped>
.td-view { display: flex; flex-direction: column; flex: 1; min-height: 0; background: #f8f9fc; font-size: 14px; }

.td-state { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #64748b; padding: 24px; text-align: center; }
.td-state__spinner { font-size: 1.8rem; animation: td-spin 0.8s linear infinite; }
.td-state__icon { font-size: 2rem; color: #94a3b8; }
.td-state p { margin: 0; max-width: 360px; font: 400 14px var(--font-body); }
@keyframes td-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }

.td-body { flex: 1; overflow-y: auto; padding: 12px 16px 24px; }
.td-body::-webkit-scrollbar { width: 6px; }
.td-body::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 3px; }
.td-body::-webkit-scrollbar-track { background: transparent; }
.td-column { max-width: 600px; margin: 0 auto; display: flex; flex-direction: column; gap: 12px; }

.td-block { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; }
.td-label { font: 500 11px var(--font-body); color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
.td-label--inline { margin-bottom: 0; }

.td-title { margin: 0 0 8px; font-family: var(--font-display); font-weight: 700; font-size: 18px; color: #1e3a5f; line-height: 1.3; }
.td-description { margin: 0; font: 400 14px var(--font-body); color: #64748b; line-height: 1.5; white-space: pre-wrap; }
.td-description--empty { font-style: italic; color: #94a3b8; }
.td-gallery { display: flex; gap: 8px; overflow-x: auto; margin-top: 12px; padding-bottom: 4px; }
.td-gallery__thumb { height: 80px; width: auto; border-radius: 6px; flex-shrink: 0; cursor: pointer; object-fit: cover; }

.td-location { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 64px; }
.td-location__tenant { font: 500 14px var(--font-body); color: #1e3a5f; }
.td-location__meta { display: flex; align-items: center; gap: 8px; margin-top: 4px; font: 400 12px var(--font-body); color: #64748b; flex-wrap: wrap; }
.td-location__notes { margin-top: 6px; font: 400 12px var(--font-body); color: #94a3b8; }
.td-tag { background: #f1f5f9; color: #64748b; border-radius: 4px; padding: 1px 7px; font: 500 11px var(--font-body); }
.td-location__map { font: 500 13px var(--font-body); color: #3b82f6; text-decoration: none; white-space: nowrap; }

.td-status { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.td-status__pill { height: 32px; padding: 0 18px; border-radius: 16px; color: #fff; font: 600 13px var(--font-body); display: inline-flex; align-items: center; }
.td-status__date { font: 400 11px var(--font-body); color: #94a3b8; }
.td-status__sla { font: 500 12px var(--font-body); }

.td-stages { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.td-stage { height: 44px; border-radius: 22px; border: 1.5px solid; background: #fff; cursor: pointer; font: 600 13px var(--font-body); }
.td-field { display: flex; flex-direction: column; gap: 6px; margin-top: 14px; }
.td-field__label { font: 500 12px var(--font-body); color: #334155; }
.td-required { color: #dc2626; }
.td-textarea { width: 100%; padding: 10px; border: 1px solid #e2e8f0; border-radius: 6px; font: 400 14px var(--font-mono); color: #334155; resize: vertical; }
.td-textarea:focus { outline: none; border-color: #3b82f6; }
.td-attach { display: flex; align-items: center; gap: 10px; margin-top: 10px; flex-wrap: wrap; }
.td-attach__name { font: 400 11px var(--font-body); color: #64748b; word-break: break-all; }
.td-hidden { display: none; }
.td-note { font: 400 13px var(--font-body); color: #94a3b8; }
.td-note--warn { color: #b45309; background: #fef3c7; border-radius: 6px; padding: 8px 10px; }
.td-error { font: 400 12px var(--font-body); color: #dc2626; margin-top: 8px; }

.td-btn { height: 36px; padding: 0 14px; border-radius: 6px; cursor: pointer; font: 500 13px var(--font-body); display: inline-flex; align-items: center; justify-content: center; }
.td-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.td-btn--primary { background: #3b82f6; border: 1px solid #3b82f6; color: #fff; }
.td-btn--outline { background: #fff; border: 1px solid #e2e8f0; color: #334155; }
.td-btn--block { width: 100%; margin-top: 12px; }
.td-btn--inline { margin-top: 4px; }

.td-thread { display: flex; flex-direction: column; gap: 8px; max-height: 320px; overflow-y: auto; padding-right: 2px; }
.td-bubble-row { display: flex; }
.td-bubble-row--mine { justify-content: flex-end; }
.td-bubble { max-width: 82%; background: #f1f5f9; color: #334155; border-radius: 12px 12px 12px 2px; padding: 8px 12px; }
.td-bubble--mine { background: #3b82f6; color: #fff; border-radius: 12px 12px 2px 12px; }
.td-bubble__author { font: 600 11px var(--font-body); color: #64748b; margin-bottom: 2px; }
.td-bubble__text { font: 400 14px var(--font-body); line-height: 1.4; white-space: pre-wrap; word-break: break-word; }
.td-bubble__img { display: block; max-width: 100%; max-height: 160px; border-radius: 8px; margin-top: 6px; cursor: pointer; }
.td-bubble__file { display: block; margin-top: 6px; font: 500 12px var(--font-body); color: inherit; text-decoration: underline; }
.td-bubble__time { font: 400 10px var(--font-body); opacity: 0.65; margin-top: 4px; text-align: right; }
.td-auto { text-align: center; font: 400 11px var(--font-body); color: #94a3b8; font-style: italic; }

.td-composer { display: flex; gap: 8px; margin-top: 12px; }
.td-composer__input { flex: 1; min-width: 0; height: 44px; padding: 0 12px; border: 1px solid #e2e8f0; border-radius: 22px; font: 400 14px var(--font-body); color: #334155; }
.td-composer__input:focus { outline: none; border-color: #3b82f6; }
.td-composer__send { width: 44px; height: 44px; border-radius: 50%; border: none; background: #3b82f6; color: #fff; font-size: 18px; cursor: pointer; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.td-composer__send:disabled { opacity: 0.5; cursor: not-allowed; }

.td-cost { display: flex; align-items: center; justify-content: space-between; }
.td-cost__amount { font: 500 14px var(--font-mono); color: #1e3a5f; }

.td-lightbox { position: fixed; inset: 0; z-index: 4000; background: rgba(15, 23, 42, 0.92); display: flex; align-items: center; justify-content: center; padding: 16px; }
.td-lightbox img { max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 6px; }
.td-lightbox__close { position: absolute; top: 12px; right: 12px; width: 40px; height: 40px; border-radius: 50%; border: none; background: rgba(255, 255, 255, 0.15); color: #fff; font-size: 20px; cursor: pointer; }

.td-toast {
  position: fixed; left: 50%; transform: translateX(-50%); bottom: 76px; z-index: 3000;
  background: #10b981; color: #fff; padding: 10px 16px; border-radius: 6px; font: 500 13px var(--font-body);
  display: flex; align-items: center; gap: 6px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

@media (max-width: 768px) {
  .td-btn { height: 44px; }
  .td-body { padding-bottom: 24px; }
  .td-column { max-width: none; }
}
</style>